import { ApiService } from '@services/ApiService'
import OptionApi from '@services/OptionApi'
import appStore from '@stores/appStore'
import { FROM_MODULE_TYPE, MAX_RESULT_COUNT } from '@utils/constants'
import { showErrorForDev } from '@utils/errors'
import { downloadFileFromResponse, mapParamsFetchData } from '@utils/functions'
import dayjs from 'dayjs'
import { debounce } from 'lodash-es'
import { action, computed, makeObservable, observable } from 'mobx'

const defaultFilters = {
  includeXeroUploadedInvoiceOnly: false,
  showProviderPayment: localStorage.getItem('showProviderPayment') == 'true',
  specialistId: null,
  requestDateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  requestDateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  requestDateRelative: null,
  monthPeriod: dayjs().add(-1, 'month').format('MMMM'),
  yearPeriod:
    dayjs().month() === 0
      ? dayjs().add(-1, 'year').year().toString()
      : dayjs().year().toString(),
  skipCount: 0,
  maxResultCount: 20,
  sorting: 'FullName',
  dateTypeToFilter: 1, //InvoiceDate
  actionRequiredDueDateFrom: null,
  actionRequiredDueDateTo: null,
  actionRequiredDueDateRelative: null,
  actionRequiredById: null,
  commissionPaymentStatus: null,
  paymentDate: null,
}

class Store {
  static storeName = 'SpecialistRemittanceStore'
  constructor() {
    makeObservable(this, {
      loading: observable,
      data: observable,
      total: observable,
      searchFilters: observable,
      specialistData: observable,
      specialistIds: observable,
      totalRemittanceAmout: computed,
      open: observable,
      modalParams: observable,
      changeFiltersValue: action,
      setFieldsValue: action,
      fetch: action,
      filters: action,
      resetStore: action,
    })
  }

  loading = false
  data = []
  total = 0
  searchFilters = { ...defaultFilters }
  specialistData = []
  specialistIds = []
  open = false
  openModalViewCheckedStatement = false
  modalParams: any = {}
  dataActionPIC = []
  resetStore = () => {
    this.loading = false
    this.data = []
    this.total = 0
    this.searchFilters = { ...defaultFilters }
    this.specialistData = []
    this.specialistIds = []
    this.open = false
    this.modalParams = {}
    this.fetch()
  }
  get totalRemittanceAmout() {
    let amount = 0

    if (this.specialistIds && this.specialistIds.length > 0) {
      this.specialistIds.forEach(sId => {
        const dSeleted = this.data.find(i => i.id === sId)

        if (dSeleted) {
          amount += dSeleted.commissionAmountWithTax
        }
      })
    }

    return appStore.formatMoney(amount)
  }

  filters(customFilters = {}) {
    try {
      const params: any = mapParamsFetchData({
        ...this.searchFilters,
        ...customFilters,
      })
      const date = dayjs(
        `${params.monthPeriod}${params.yearPeriod}`,
        'MMMMYYYY'
      )
      params.paymentPeriod = date.startOf('month').format('YYYY-MM-DD')

      // delete params.monthPeriod
      // delete params.yearPeriod
      return params
    } catch (e) {
      this.loading = false
    }
  }

  changeFiltersValue = (obj, refresh = true) => {
    let reset = refresh

    Object.keys(obj).forEach(key => {
      this.searchFilters[key] = obj[key]
      if (key === 'monthPeriod' || key === 'yearPeriod') {
        //case choose both monthPeriod & yearPeriod
        if (this.searchFilters.monthPeriod && this.searchFilters.yearPeriod) {
          const date = dayjs(
            `${this.searchFilters.monthPeriod}${this.searchFilters.yearPeriod}`,
            'MMMMYYYY'
          )
          this.searchFilters.requestDateFrom = date
            .startOf('month')
            .format('YYYY-MM-DD')
          this.searchFilters.requestDateTo = date
            .endOf('month')
            .format('YYYY-MM-DD')
        }
        //case choose only yearPeriod
        else if (this.searchFilters.yearPeriod) {
          const date = dayjs(`${this.searchFilters.yearPeriod}`, 'YYYY')
          this.searchFilters.requestDateFrom = date
            .startOf('year')
            .format('YYYY-MM-DD')
          this.searchFilters.requestDateTo = date
            .endOf('year')
            .format('YYYY-MM-DD')
        }
        //case choose only monthPeriod
        else {
          this.searchFilters.requestDateFrom = null
          this.searchFilters.requestDateTo = null
        }
      }

      if (key === 'skipCount') {
        reset = false
      }
    })

    if (reset) {
      this.searchFilters.skipCount = 0
      this.specialistIds = []
    }
  }

  setFieldsValue = obj => {
    Object.keys(obj).forEach(key => {
      this[key] = obj[key]
    })
  }

  fetchSpecialist = (filterText?) => {
    OptionApi.Specialist({ filterText }).then(res => {
      this.specialistData = res?.data?.items
    })
  }

  fetch = () => {
    this.loading = true
    ApiService.Specialist.SpecialistWithPayment(this.filters())
      .then(res => {
        this.data = res.data?.items || []
        this.total = res.data?.totalCount || 0
      })
      .finally(
        action(() => {
          this.loading = false
        })
      )
  }

  searchOptions = fieldName =>
    debounce(filterText => {
      if (fieldName === 'actionRequired') {
        this.fetchActionPIC({ filterText })
      }
      if (fieldName === 'specialist') {
        this.fetchSpecialist({ filterText })
      }
    }, 500)

  fetchActionPIC = ({ filterText = '' }) => {
    ApiService.ActionRequired.GetAssignedUsersCount({
      fromModule: FROM_MODULE_TYPE.PROVIDER_REMITTANCE,
      filterText,
      maxResultCount: MAX_RESULT_COUNT,
    }).then(
      action(res => {
        const dataFormat = []
        res.data?.items?.forEach(e => {
          dataFormat.push({
            displayName: `${e.userName} ${e.userSurname}`,
            // displayName: `${e.userName} ${e.userSurname} (${e.count})`,
            id: e.userId,
            isActive: e.isActive,
          })
        })
        this.dataActionPIC = dataFormat
      })
    )
  }
  toggleModal = (isOpen, params = {}) => {
    action(() => {
      this.open = isOpen
      this.modalParams = params
    })()
  }

  export = (customFilters = {}, specialistName = 'Specialist') => {
    return new Promise((resolve, reject) => {
      ApiService.Specialist.ExportSpecialistWithPayment(
        this.filters(customFilters)
      )
        .then(res => {
          downloadFileFromResponse(res, `${specialistName} - Remittance.xlsx`)
          resolve(null)
        })
        .catch(err => reject(err))
    })
  }

  exportRemittanceList = (customFilters = {}) => {
    return appStore.openConfirmModal({
      message: 'Do you want to download search results?',
      onOk: () => {
        this.loading = true
        ApiService.Specialist.ExportSpecialistRemittanceList(
          this.filters(customFilters)
        )
          .then(res => {
            downloadFileFromResponse(res, `Specialist Remittance.xlsx`)
          })
          .catch(err => {
            showErrorForDev(err)
          })
          .finally(
            action(() => {
              this.loading = false
            })
          )
      },
    })
  }

  print = (customFilters = {}) => {
    return new Promise((resolve, reject) => {
      ApiService.Specialist.PrintSpecialistWithPayment(
        this.filters(customFilters)
      )
        .then(res => {
          resolve(res.data)
        })
        .catch(err => reject(err))
    })
  }

  sendCommissionRemittanceStatementToSpecialist = () => {
    const params = this.filters()

    return ApiService.Specialist.SendCommissionRemittanceStatementToSpecialist(
      this.specialistIds,
      {
        invoiceDateFrom: params.requestDateFrom,
        invoiceDateTo: params.requestDateTo,
        paymentPeriod: params.paymentPeriod,
        showProviderPayment: params.showProviderPayment,
      }
    )
  }

  handleView = specialistId => {
    const params = this.filters({ specialistId: specialistId })
    const queryString = new URLSearchParams(params).toString()
    window.open(`/view/specialist-remittance/print?${queryString}`, '_blank')
  }

  handleViewCheckedSpecialist = () => {
    this.specialistIds.forEach(specialistId => {
      this.handleView(specialistId)
    })
  }
}

export default new Store()
